import React, { useState, useEffect } from 'react';
import { Snackbar, Alert, Box, Typography, IconButton, Button, CircularProgress, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

function UploadProgressSnackbar({
  open,
  uploadProgress,
  allUploadsCompleted,
  onViewReceipt,
  onClose,
  onRetry,
  resetDismissed
}) {
    const [showExpanded, setShowExpanded] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const navigate = useNavigate();
    
    // Reset dismissed state when resetDismissed changes
    useEffect(() => {
        setDismissed(false);
    }, [resetDismissed]);

    // Auto-dismiss after N seconds when all uploads are completed
    useEffect(() => {
        if (allUploadsCompleted && !dismissed) {
            const timer = setTimeout(() => {
                handleClose();
            }, 15000); // 15 seconds

            return () => clearTimeout(timer);
        }
    }, [allUploadsCompleted, dismissed]);
    
    const toggleShowExpanded = () => {
        setShowExpanded(prev => !prev);
    };
    
    const handleClose = () => {
        setDismissed(true);
        if (onClose) {
            onClose();
        }
    };

    // Calculate status counts
    const completedCount = uploadProgress.filter(file => file.status === 'completed').length;
    const errorCount = uploadProgress.filter(file => file.status === 'error').length;
    const uploadingCount = uploadProgress.filter(file => file.status === 'uploading').length;
    
    // Determine the overall status
    let overallStatus = "info";
    let statusMessage = "";
    
    if (uploadingCount > 0) {
        overallStatus = "info";
        statusMessage = `Uploading ${uploadProgress.length} Receipts`;
    } else if (errorCount > 0 && completedCount > 0) {
        overallStatus = "warning";
        statusMessage = `${completedCount} Completed, ${errorCount} Failed`;
    } else if (errorCount > 0 && completedCount === 0) {
        overallStatus = "error";
        statusMessage = "Upload Failed";
    } else if (completedCount > 0 && errorCount === 0) {
        overallStatus = "success";
        statusMessage = "Uploads Complete";
    }

    // Don't show if dismissed manually
    if (dismissed) {
        return null;
    }

    const handleViewReceipt = (receiptUuid) => {
        navigate(`/receipts/${receiptUuid}`);
    };

    return (
        <Snackbar
        open={open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ 
            width: { xs: '95vw', sm: '80vw' },
            maxWidth: '600px',
            margin: '0 auto',
            transition: 'max-height 0.3s ease-in-out',
            bottom: { xs: 20, sm: 24 },
        }}
        >
        <Alert
            severity={overallStatus}
            sx={{ 
            width: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            padding: '8px 16px',
            maxHeight: showExpanded ? '300px' : '48px',
            overflow: 'hidden',
            boxShadow: 3,
            borderRadius: 1,
            '& .MuiAlert-message': {
                width: '100%',
                padding: 0
            },
            '& .MuiAlert-icon': {
                padding: '8px 0',
                marginRight: 1
            }
            }}
            action={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {uploadingCount === 0 && (
                    <IconButton 
                        color="inherit" 
                        size="small" 
                        onClick={handleClose}
                        sx={{ mr: 0.5 }}
                        aria-label="close"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
                <IconButton 
                    color="inherit" 
                    size="small" 
                    onClick={toggleShowExpanded}
                    aria-label={showExpanded ? "collapse" : "expand"}
                >
                    {showExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>
            }
        >
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                flexGrow: 1, 
                justifyContent: 'space-between',
                flexWrap: 'nowrap'
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mr: 2,
                    minWidth: 0,
                    overflow: 'hidden'
                }}>
                    {uploadingCount > 0 && (
                        <CircularProgress size={20} sx={{ mr: 2, flexShrink: 0, color: 'primary.main' }} />
                    )}
                    <Typography 
                        variant="subtitle1" 
                        sx={{ 
                            fontWeight: 'bold',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {statusMessage}
                    </Typography>
                </Box>
                
                {/* Status chips */}
                {!showExpanded && (
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        ml: 'auto', 
                        flexShrink: 0,
                        flexWrap: 'nowrap'
                    }}>
                        {completedCount > 0 && (
                            <Chip 
                                size="small" 
                                color="success" 
                                label={`${completedCount} Completed`} 
                                icon={<CheckCircleIcon />}
                                sx={{ height: 24 }}
                            />
                        )}
                        {errorCount > 0 && (
                            <Chip 
                                size="small" 
                                color="error" 
                                label={`${errorCount} Failed`} 
                                icon={<ErrorIcon />}
                                sx={{ height: 24 }}
                            />
                        )}
                    </Box>
                )}
            </Box>
            
            {showExpanded && (
            <Box sx={{ maxHeight: 250, overflowY: 'auto', mt: 1, width: '100%' }}>
                {uploadProgress.map((file, index) => (
                <Box
                    key={file.fileId || index}
                    sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: file.status !== 'error' ? 'center' : 'flex-start',
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: file.status === 'error' ? 'rgba(211, 47, 47, 0.1)' : 'transparent',
                    width: '100%',
                    }}
                >
                    {file.status === 'completed' && (
                        <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                    )}
                    {file.status === 'error' && (
                        <ErrorIcon sx={{ color: 'error.main', mr: 1, mt: 0.5 }} />
                    )}
                    {file.status === 'uploading' && (
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                    )}
                    
                    {file.status !== 'error' && (
                        <Typography variant="body2" noWrap sx={{ flexGrow: 1, textAlign: 'left' }}>
                            {file.fileName}
                        </Typography>
                    )}
                    
                    {file.status === 'completed' && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => handleViewReceipt(file.receiptUuid)}
                            sx={{ ml: 'auto' }}
                        >
                            View
                        </Button>
                    )}
                    {file.status === 'uploading' && (
                        <Typography variant="body2" color="primary" sx={{ ml: 'auto' }}>
                            Uploading...
                        </Typography>
                    )}
                    {file.status === 'error' && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <Typography variant="body2" noWrap sx={{ flexGrow: 1, color: 'error.main', textAlign: 'left' }}>
                                    {file.fileName}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    onClick={() => {
                                        // Call the retry function with the file
                                        if (onRetry) {
                                            onRetry(file);
                                        }
                                    }}
                                    sx={{ ml: 'auto' }}
                                >
                                    Retry
                                </Button>
                            </Box>
                            {file.errorMessage && (
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: 'error.main', 
                                        mt: 0.5,
                                        display: 'block',
                                        backgroundColor: 'rgba(211, 47, 47, 0.05)',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        borderLeft: '2px solid',
                                        borderColor: 'error.main',
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    <strong>Error:</strong> {file.errorMessage}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
                ))}
            </Box>
            )}
        </Alert>
        </Snackbar>
    );
}

export default UploadProgressSnackbar;
